import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { personalizedSlipsAccessible } from "@redotech/redo-fulfillment-common/personalized-slip/personalized-slip-access";
import { FulfillmentGroupData } from "@redotech/redo-fulfillment-group-common/fulfillment-group";
import { FulfillmentGroupStatus } from "@redotech/redo-fulfillment-group-common/fulfillment-group-status";
import { PrintKind } from "@redotech/redo-fulfillment-group-common/fulfillment-print-status";
import { PrintableDocument } from "@redotech/redo-fulfillment-group-common/print-job";
import { summaryPackages } from "@redotech/redo-fulfillment-group-common/shipment-summary";
import MenuIcon from "@redotech/redo-icons/dots-horizontal.svg";
import PrintIcon from "@redotech/redo-icons/printer.svg";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoCommandMenu } from "@redotech/redo-web/arbiter-components/command-menu/redo-command-menu";
import { Tooltip } from "@redotech/redo-web/arbiter-components/tooltip/tooltip";
import { Flex } from "@redotech/redo-web/flex";
import { PillTheme } from "@redotech/redo-web/pill";
import { TagPill } from "@redotech/redo-web/tags/tag-pill";
import { memo, useContext, useMemo, useState } from "react";
import {
  getDocumentPrintStatusMapForFulfillmentGroups,
  PrintBatchContext,
} from "../../common/print-batch-modal";
import { RedoOutboundLabelsRpcClientContext } from "../../outbound-labels-rpc-client";
import { getEffectiveShipmentSummary } from "../../utils/effective-shipment-summary";
import { useFulfillmentGroupPermissions } from "../../utils/use-assigned-fulfillment-service";
import { printKindToTitle } from "../columns/is-printed-column";
import * as isPrintedCellCss from "./is-printed-cell.module.css";

const canPrint = (
  kind: PrintKind,
  row: FulfillmentGroupData,
  personalizedSlipsEnabled: boolean,
) => {
  // tslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check -- TODO: grandfather FIXME
  switch (kind) {
    case PrintKind.LABEL:
      return row.status === FulfillmentGroupStatus.Closed;
    case PrintKind.PERSONALIZED_SLIP:
      return personalizedSlipsEnabled && !!row.personalizedSlipTemplateId;
    case PrintKind.PACKING_SLIP:
    case PrintKind.PICK_LIST:
    case PrintKind.ORDER_SUMMARY:
      return true;
    default:
      return false;
  }
};

const canMarkUnprinted = (kind: PrintKind) => kind !== PrintKind.LABEL;

const PrintKindToPrintableDocument: Record<PrintKind, PrintableDocument> = {
  [PrintKind.PICK_LIST]: PrintableDocument.PICK_LIST,
  [PrintKind.PACKING_SLIP]: PrintableDocument.PACKING_SLIP,
  [PrintKind.LABEL]: PrintableDocument.LABEL,
  [PrintKind.COMMERCIAL_INVOICE]: PrintableDocument.COMMERCIAL_INVOICE,
  [PrintKind.PERSONALIZED_SLIP]: PrintableDocument.PERSONALIZED_SLIP,
  [PrintKind.RETURN_LABEL]: PrintableDocument.RETURN_LABEL,
  [PrintKind.LOCAL_PICK_UP_LABEL]: PrintableDocument.LOCAL_PICK_UP_LABEL,
  [PrintKind.BILL_OF_LADING]: PrintableDocument.BILL_OF_LADING,
  [PrintKind.PARCEL_PACKING_SLIP]: PrintableDocument.PARCEL_PACKING_SLIP,
  [PrintKind.ORDER_SUMMARY]: PrintableDocument.ORDER_SUMMARY,
};

export function IsPrintedCell({
  row,
  kind,
}: {
  row: FulfillmentGroupData;
  kind: PrintKind;
}) {
  const batchPrinting = useContext(PrintBatchContext);
  const team = useRequiredContext(TeamContext);
  const { permissionsToEditEveryGroup } = useFulfillmentGroupPermissions([row]);
  const rpcClient = useRequiredContext(RedoOutboundLabelsRpcClientContext);

  const personalizedSlipsEnabled = personalizedSlipsAccessible(
    team?.settings?.outboundLabels,
  );
  const documentPrintStatusMap = getDocumentPrintStatusMapForFulfillmentGroups([
    row,
  ]);

  // The personalized insert PDF renders asynchronously after selection; block
  // printing just that insert until generation finishes (other docs are fine).
  const isGeneratingInsert =
    kind === PrintKind.PERSONALIZED_SLIP &&
    !!row.personalizedSlipGeneratingSince;

  const doPrint = useMemo(
    () => ({
      [PrintKind.PICK_LIST]: async () => {
        batchPrinting.printBatch({
          batch: { fulfillmentGroupIds: [row._id] },
          document: PrintableDocument.PICK_LIST,
          documentPrintStatusMap,
        });
      },
      [PrintKind.PACKING_SLIP]: async () => {
        const packingSlipIds = row.packingSlipTemplateId
          ? [row.packingSlipTemplateId]
          : team?.settings?.outboundLabels?.packingSlipId
            ? [team?.settings?.outboundLabels?.packingSlipId]
            : [];

        batchPrinting.printBatch({
          batch: { fulfillmentGroupIds: [row._id] },
          document: PrintableDocument.PACKING_SLIP,
          documentPrintStatusMap,
          packingSlipIds,
        });
      },
      [PrintKind.LABEL]: async () => {
        const packingSlipIds = row.packingSlipTemplateId
          ? [row.packingSlipTemplateId]
          : [];

        const personalizedSlipIds =
          personalizedSlipsEnabled && row.personalizedSlipTemplateId
            ? [row.personalizedSlipTemplateId]
            : [];

        batchPrinting.printBatch({
          batch: { fulfillmentGroupIds: [row._id] },
          document: PrintableDocument.LABEL,
          documentPrintStatusMap,
          packingSlipIds,
          personalizedSlipIds,
        });
      },
      [PrintKind.COMMERCIAL_INVOICE]: async () => {
        batchPrinting.printBatch({
          batch: { fulfillmentGroupIds: [row._id] },
          document: PrintableDocument.COMMERCIAL_INVOICE,
          documentPrintStatusMap,
        });
      },
      [PrintKind.PERSONALIZED_SLIP]: async () => {
        const personalizedSlipIds =
          personalizedSlipsEnabled && row.personalizedSlipTemplateId
            ? [row.personalizedSlipTemplateId]
            : [];

        batchPrinting.printBatch({
          batch: { fulfillmentGroupIds: [row._id] },
          document: PrintableDocument.PERSONALIZED_SLIP,
          documentPrintStatusMap,
          personalizedSlipIds,
        });
      },
      [PrintKind.RETURN_LABEL]: async () => {
        batchPrinting.printBatch({
          batch: { fulfillmentGroupIds: [row._id] },
          document: PrintableDocument.RETURN_LABEL,
          documentPrintStatusMap,
        });
      },
      [PrintKind.LOCAL_PICK_UP_LABEL]: async () => {
        batchPrinting.printBatch({
          batch: { fulfillmentGroupIds: [row._id] },
          document: PrintableDocument.LOCAL_PICK_UP_LABEL,
          documentPrintStatusMap,
        });
      },
      [PrintKind.BILL_OF_LADING]: async () => {
        batchPrinting.printBatch({
          batch: { fulfillmentGroupIds: [row._id] },
          document: PrintableDocument.BILL_OF_LADING,
          documentPrintStatusMap,
        });
      },
      [PrintKind.PARCEL_PACKING_SLIP]: async () => {
        batchPrinting.printBatch({
          batch: { fulfillmentGroupIds: [row._id] },
          document: PrintableDocument.PARCEL_PACKING_SLIP,
          documentPrintStatusMap,
        });
      },
      [PrintKind.ORDER_SUMMARY]: async () => {
        batchPrinting.printBatch({
          batch: { fulfillmentGroupIds: [row._id] },
          document: PrintableDocument.ORDER_SUMMARY,
          documentPrintStatusMap,
        });
      },
    }),
    // FIXME
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [row._id, batchPrinting.printBatch, row.printStatus],
  );

  const doMarkUnprinted = useHandler(async () => {
    await rpcClient.markAsPrinted({
      document: PrintKindToPrintableDocument[kind],
      unprint: true,
      groups: { fulfillmentGroupIds: [row._id] },
    });
  });

  const tooltipTitle = useMemo(() => {
    if (
      canMarkUnprinted(kind) &&
      row.printStatus?.[kind] &&
      permissionsToEditEveryGroup
    ) {
      return null;
    }

    if (
      kind === PrintKind.LABEL &&
      row.status !== FulfillmentGroupStatus.Closed
    ) {
      return null;
    }

    return `${row.printStatus?.[kind] ? "Reprint" : "Print"} ${printKindToTitle[kind]}`;
    // FIXME
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onPrintHandler = useHandler((e: React.MouseEvent) => {
    if (
      canPrint(kind, row, personalizedSlipsEnabled) &&
      permissionsToEditEveryGroup &&
      !isGeneratingInsert
    ) {
      e.stopPropagation();
      void doPrint[kind]();
    }
  });

  const onMarkUnprintedHandler = useHandler((e: React.MouseEvent) => {
    if (
      canMarkUnprinted(kind) &&
      row.printStatus?.[kind] &&
      permissionsToEditEveryGroup
    ) {
      e.stopPropagation();
      void doMarkUnprinted();
    }
  });

  const actionIcon = useMemo(() => {
    const showPrintAction =
      canPrint(kind, row, personalizedSlipsEnabled) &&
      permissionsToEditEveryGroup;
    const showMarkUnprintedAction =
      canMarkUnprinted(kind) &&
      row.printStatus?.[kind] &&
      permissionsToEditEveryGroup;

    if (!showPrintAction && !showMarkUnprintedAction) {
      return null;
    }

    if (showPrintAction && !showMarkUnprintedAction) {
      return <PrintIcon className={isPrintedCellCss.printerIcon} />;
    }

    return (
      <PrintCellCommandMenu
        onMarkUnprinted={onMarkUnprintedHandler}
        onPrint={onPrintHandler}
        printingDisabled={batchPrinting.disabled || isGeneratingInsert}
      />
    );
  }, [
    permissionsToEditEveryGroup,
    kind,
    row,
    onPrintHandler,
    onMarkUnprintedHandler,
    batchPrinting.disabled,
    personalizedSlipsEnabled,
    isGeneratingInsert,
  ]);

  return kind !== PrintKind.LABEL ||
    summaryPackages(getEffectiveShipmentSummary(row)).length ? (
    <Tooltip
      title={
        isGeneratingInsert ? "Generating personalized insert…" : tooltipTitle
      }
    >
      <div className={isPrintedCellCss.isPrintedCell} onClick={onPrintHandler}>
        <Flex align="center" gap="sm" justify="space-between" p="lg">
          {row.printStatus?.[kind] ? (
            <TagPill
              badgeSize="xs"
              tag={{ name: "Yes", pillTheme: PillTheme.SUCCESS }}
            />
          ) : (
            <TagPill
              badgeSize="xs"
              tag={{ name: "No", pillTheme: PillTheme.NORMAL }}
            />
          )}
          {actionIcon}
        </Flex>
      </div>
    </Tooltip>
  ) : null;
}

const PrintCellCommandMenu = memo(function PrintCellCommandMenu({
  onPrint,
  onMarkUnprinted,
  printingDisabled,
}: {
  onPrint: (e: React.MouseEvent) => void;
  onMarkUnprinted: (e: React.MouseEvent) => void;
  printingDisabled?: boolean;
}) {
  const [contextMenuAnchor, setContextMenuAnchor] =
    useState<HTMLElement | null>(null);
  const [contextMenuOpen, setContextMenuOpen] = useState(false);
  return (
    <>
      <RedoButton
        aria-label="More options"
        hierarchy="tertiary"
        IconLeading={MenuIcon}
        onClick={(event) => {
          setContextMenuOpen(true);
          event.stopPropagation();
        }}
        ref={setContextMenuAnchor}
        size="sm"
        theme="normal"
      />

      <RedoCommandMenu
        anchor={contextMenuAnchor}
        items={[
          { text: "Print", onClick: onPrint, disabled: printingDisabled },
          { text: "Mark unprinted", onClick: onMarkUnprinted },
        ]}
        open={contextMenuOpen}
        setOpen={setContextMenuOpen}
      />
    </>
  );
});
